"use client"

import { CookieSettingsLink } from "@webscraping/consent"
import { useTranslations } from "next-intl"
import type { ComponentProps } from "react"
import { H6 } from "~/components/legacy/common/heading"
import { BrandLinkedInIcon } from "~/components/legacy/common/icons/brand-linkedin"
import { BrandXIcon } from "~/components/legacy/common/icons/brand-x"
import { Stack } from "~/components/legacy/common/stack"
import { Tooltip } from "~/components/legacy/common/tooltip"
import { ThemeSwitcher } from "~/components/legacy/web/theme-switcher"
import { NavLink, navLinkVariants } from "~/components/legacy/web/ui/nav-link"
import { AtSignIcon, RssIcon } from "~/components/ui/icons"
import { ExternalLink } from "~/components/web/external-link"
import { linksConfig } from "~/config/links"
import {
  browseNavigationLinks,
  companyNavigationLinks,
  freeToolNavigationLinks,
  priorityHubLinks,
} from "~/config/navigation-links"
import { siteConfig } from "~/config/site"
import { cx } from "~/lib/legacy-utils"

type FooterProps = ComponentProps<"div">

export const Footer = ({ children, className, ...props }: FooterProps) => {
  const t = useTranslations()

  return (
    <footer className="flex shrink-0 flex-col mt-auto">
      <hr className="-mx-fluid-md border-border" />

      <div className="pt-12 md:pt-16">
        <div
          className={cx("grid grid-cols-2 gap-y-10 gap-x-6 lg:grid-cols-4", className)}
          {...props}
        >
          {/* Browse column */}
          <Stack direction="column" className="text-sm">
            <H6 as="strong" className="font-medium text-muted-foreground">
              {t("navigation.browse")}
            </H6>

            {browseNavigationLinks.map(({ href, labelKey }) => (
              <NavLink key={href} href={href}>
                {t(labelKey)}
              </NavLink>
            ))}
          </Stack>

          {/* Guides column */}
          <Stack direction="column" className="text-sm">
            <H6 as="strong" className="font-medium text-muted-foreground">
              {t("navigation.hubs")}
            </H6>

            {priorityHubLinks.map(({ href, label }) => (
              <NavLink key={href} href={href}>
                {label}
              </NavLink>
            ))}
          </Stack>

          <Stack direction="column" className="text-sm">
            <H6 as="strong" className="font-medium text-muted-foreground">
              {t("navigation.free_tools")}
            </H6>
            {freeToolNavigationLinks.map(({ href, labelKey }) => (
              <NavLink key={href} href={href}>
                {t(labelKey)}
              </NavLink>
            ))}
          </Stack>

          {/* Company column */}
          <Stack direction="column" className="text-sm">
            <H6 as="strong" className="font-medium text-muted-foreground">
              {t("navigation.company")}
            </H6>

            {companyNavigationLinks.map(({ href, labelKey }) => (
              <NavLink key={href} href={href}>
                {t(labelKey)}
              </NavLink>
            ))}
          </Stack>
        </div>

        {children}
      </div>

      {/* Bottom bar */}
      <hr className="-mx-fluid-md mt-10 border-border" />

      <div className="pt-6 pb-[max(2rem,env(safe-area-inset-bottom))] text-xs text-muted-foreground">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <CookieSettingsLink className={navLinkVariants({ isActive: false, isPadded: true })}>
              Cookie preferences
            </CookieSettingsLink>
            <span aria-hidden className="opacity-50">
              /
            </span>
            <NavLink href="/privacy">Privacy Policy</NavLink>
            <span aria-hidden className="opacity-50">
              /
            </span>
            <NavLink href="/subprocessors">Subprocessors</NavLink>
          </div>

          <Stack className="text-lg opacity-75">
            <Tooltip tooltip={t("navigation.toggle_theme")}>
              <ThemeSwitcher />
            </Tooltip>

            <Tooltip tooltip={t("navigation.x_twitter")}>
              <ExternalLink href={linksConfig.x} className={navLinkVariants()}>
                <BrandXIcon />
              </ExternalLink>
            </Tooltip>

            <Tooltip tooltip={t("navigation.linkedin")}>
              <ExternalLink href={linksConfig.linkedin} className={navLinkVariants()}>
                <BrandLinkedInIcon />
              </ExternalLink>
            </Tooltip>

            <Tooltip tooltip={t("navigation.rss_latest_tools")}>
              <ExternalLink
                aria-label={t("navigation.rss_latest_tools")}
                href={linksConfig.feed}
                className={navLinkVariants()}
              >
                <RssIcon />
              </ExternalLink>
            </Tooltip>

            <Tooltip tooltip={t("navigation.contact_us")}>
              <ExternalLink
                aria-label={t("navigation.contact_us")}
                href={`mailto:${siteConfig.email}`}
                className={navLinkVariants()}
              >
                <AtSignIcon />
              </ExternalLink>
            </Tooltip>
          </Stack>
        </div>

        <hr className="-mx-fluid-md my-5 border-border" />

        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <span>
            &copy; {new Date().getFullYear()} {siteConfig.name}
          </span>
        </div>
      </div>
    </footer>
  )
}
